import { SLAB_ASSETS } from "../../config/slab-assets";
import { seeded } from "../../lib/motion";
import { usePrefersReducedMotion } from "../../hooks/useBreakpoint";

interface SlabWallProps {
  /** How many slabs to scatter (cycles through the asset list if higher). */
  count?: number;
  className?: string;
  /** 0-1 — how dark the overlay wash over the slabs is. Higher = more legible foreground. */
  dim?: number;
  /** Card size in px before the sm: breakpoint bump. */
  size?: number;
}

/**
 * Decorative backdrop of graded-slab photography, scattered and tilted like a
 * collector's wall, faded under a dark wash so foreground content (a login
 * form, a page title) stays fully legible on top. Always `aria-hidden` — it's
 * texture, not content.
 */
export function SlabWall({ count = 10, className = "", dim = 0.72, size = 128 }: SlabWallProps) {
  const reduced = usePrefersReducedMotion();
  const slabs = Array.from({ length: count }, (_, i) => SLAB_ASSETS[i % SLAB_ASSETS.length]!);

  return (
    <div aria-hidden className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
      <div
        className="flex h-full w-full flex-wrap items-center justify-center gap-3 p-2 sm:gap-4"
        style={{ filter: "saturate(0.85)" }}
      >
        {slabs.map((src, i) => {
          const rotate = (seeded(i, 8) - 0.5) * 26;
          const lift = (seeded(i, 13) - 0.5) * 34;
          const duration = 7 + seeded(i, 21) * 5;
          const delay = seeded(i, 17) * 4;
          return (
            <img
              key={`${src}-${i}`}
              src={src}
              alt=""
              draggable={false}
              loading="lazy"
              className="aspect-[5/7] rounded-lg object-cover shadow-2xl shadow-black/60"
              style={{
                width: size,
                transform: `rotate(${rotate}deg) translateY(${lift}px)`,
                animation: !reduced ? `float-soft ${duration}s ease-in-out ${delay}s infinite` : undefined,
              }}
            />
          );
        })}
      </div>
      <div className="absolute inset-0 bg-brand-dark" style={{ opacity: dim }} />
      <div className="absolute inset-0 bg-mesh-deep opacity-70" />
    </div>
  );
}
